/**
 * Maps between API profile (Profile / ProfilePatch) and onboarding UI (OnboardingData).
 */

import type { Profile, ProfilePatch, ProfileAddress } from "@/app/api/auth-client";
import type { OnboardingData } from "@/app/components/new-user-onboarding";

/** Checkout delivery address shape (checkout-address-page). */
export interface CheckoutAddress {
  id: string;
  name: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
  isDefault?: boolean;
}

/** Map Profile.addresses to checkout Address[] for checkout flow. Uses main (is_default) as default. */
export function profileAddressesToCheckoutAddresses(addresses: ProfileAddress[]): CheckoutAddress[] {
  return addresses.map((a, i) => ({
    id: String(i),
    name: a.is_default ? "Principal" : `Endereço ${i + 1}`,
    street: a.street,
    number: a.number,
    complement: a.complement ?? undefined,
    neighborhood: a.neighborhood,
    city: a.city,
    state: a.state,
    zipCode: a.cep,
    isDefault: a.is_default,
  }));
}

/** Map backend Profile + user info to OnboardingData for app state. */
export function profileToOnboardingData(
  profile: Profile,
  email: string,
  fullName: string,
  /**
   * GET /auth/me `email_verified` when known. If defined, it wins over `profile.contact.email_verified`
   * (customer profile can be stale after verify-email until backend sync).
   */
  accountEmailVerified?: boolean
): OnboardingData {
  const defaultAddr =
    profile.addresses.find((a) => a.is_default) ?? profile.addresses[0];
  const address = defaultAddr
    ? {
        cep: defaultAddr.cep,
        street: defaultAddr.street,
        number: defaultAddr.number,
        complement: defaultAddr.complement ?? "",
        neighborhood: defaultAddr.neighborhood,
        city: defaultAddr.city,
        state: defaultAddr.state,
      }
    : {
        cep: "",
        street: "",
        number: "",
        complement: "",
        neighborhood: "",
        city: "",
        state: "SP",
      };

  const genderRaw = profile.personal.gender?.trim();
  return {
    fullName,
    preferredName: profile.personal.preferred_name ?? "",
    cpf: profile.personal.cpf,
    birthDate: profile.personal.birth_date,
    ...(genderRaw
      ? { gender: genderRaw as OnboardingData["gender"] }
      : {}),
    phone: profile.contact.phone,
    email,
    address,
    profileImage: profile.profile_image_url,
    memberSince: profile.created_at?.split("T")[0] ?? new Date().toISOString().split("T")[0],
    isEmailVerified:
      accountEmailVerified !== undefined ? accountEmailVerified : profile.contact.email_verified,
    isPhoneVerified: profile.contact.phone_verified,
    isCpfVerified: profile.personal.cpf_verified,
    onboardingCompletedAt: profile.onboarding_completed_at ?? null,
  };
}

/** True when the user has not finished onboarding (no profile row or API says onboarding not completed). */
export function isProfileIncomplete(userProfile: OnboardingData | null | undefined): boolean {
  if (userProfile == null) return true;
  const at = userProfile.onboardingCompletedAt;
  return at == null || String(at).trim() === "";
}

/**
 * True when onboarding is done but email is not verified (use merged profile state from `profileToOnboardingData` + GET /auth/me).
 */
export function isPostAdBlockedByUnverifiedEmail(userProfile: OnboardingData | null | undefined): boolean {
  if (userProfile == null) return false;
  if (isProfileIncomplete(userProfile)) return false;
  return userProfile.isEmailVerified !== true;
}

/** Map OnboardingData to ProfilePatch for PATCH /auth/me/profile. */
export function onboardingDataToProfilePatch(data: OnboardingData): ProfilePatch {
  const addr: ProfileAddress = {
    cep: data.address.cep,
    street: data.address.street,
    number: data.address.number,
    complement: data.address.complement,
    neighborhood: data.address.neighborhood,
    city: data.address.city,
    state: data.address.state,
    is_default: true,
  };

  return {
    personal: {
      cpf: data.cpf,
      cpf_verified: data.isCpfVerified,
      birth_date: data.birthDate,
      ...(data.gender ? { gender: data.gender } : {}),
      preferred_name: data.preferredName || undefined,
    },
    contact: {
      phone: data.phone,
      phone_verified: data.isPhoneVerified,
      email_verified: data.isEmailVerified,
    },
    addresses: [addr],
    // Only send http(s) URLs; never send data URLs (base64) — they can exceed body size limits and cause 400 invalid JSON
    profile_image_url: data.profileImage && (data.profileImage.startsWith("http://") || data.profileImage.startsWith("https://")) ? data.profileImage : undefined,
    onboarding_completed_at: new Date().toISOString(),
  };
}

/**
 * Address list item for My Addresses page (addresses-page).
 * id is local (index-based) for list stability; API has no address id.
 */
export interface AddressListItem {
  id: string;
  cep: string;
  rua: string;
  numero: string;
  complemento: string;
  neighborhood: string;
  city: string;
  state: string;
  isMain: boolean;
}

/** Map Profile.addresses to AddressListItem[] for the addresses page. */
export function profileAddressesToList(addresses: ProfileAddress[]): AddressListItem[] {
  return addresses.map((a, i) => ({
    id: String(i),
    cep: a.cep,
    rua: a.street,
    numero: a.number,
    complemento: a.complement ?? "",
    neighborhood: a.neighborhood,
    city: a.city,
    state: a.state,
    isMain: a.is_default,
  }));
}

/** Map AddressListItem[] to ProfileAddress[] for PATCH /auth/me/profile. Ensures exactly one is_default. */
export function addressListToProfileAddresses(
  list: AddressListItem[],
  defaultId: string
): ProfileAddress[] {
  return list.map((a) => ({
    cep: a.cep.replace(/\D/g, ""),
    street: a.rua,
    number: a.numero,
    complement: a.complemento || undefined,
    neighborhood: a.neighborhood,
    city: a.city,
    state: a.state,
    is_default: a.id === defaultId,
  }));
}

/** Edit form data shape (from EditProfilePage). */
export interface EditProfileFormData {
  fullName: string;
  preferredName: string;
  cpf: string;
  email: string;
  phone: string;
  birthDate: string;
  gender: string;
  address: { cep: string; street: string; number: string; complement?: string; neighborhood: string; city: string; state: string };
  profileImage?: string;
}

/** Map edit profile form data to ProfilePatch. Uses currentProfile for verification flags if provided. */
export function editProfileDataToProfilePatch(
  data: EditProfileFormData,
  currentProfile?: OnboardingData | null
): ProfilePatch {
  const addr: ProfileAddress = {
    cep: data.address.cep,
    street: data.address.street,
    number: data.address.number,
    complement: data.address.complement,
    neighborhood: data.address.neighborhood,
    city: data.address.city,
    state: data.address.state,
    is_default: true,
  };

  const patch: ProfilePatch = {
    personal: {
      cpf: data.cpf,
      cpf_verified: currentProfile?.isCpfVerified ?? false,
      birth_date: data.birthDate,
      gender: data.gender,
      preferred_name: data.preferredName || undefined,
    },
    contact: {
      phone: data.phone,
      phone_verified: currentProfile?.isPhoneVerified ?? false,
      email_verified: currentProfile?.isEmailVerified ?? false,
    },
    addresses: [addr],
    profile_image_url: data.profileImage && (data.profileImage.startsWith("http://") || data.profileImage.startsWith("https://")) ? data.profileImage : undefined,
  };
  const trimmedName = data.fullName?.trim();
  if (trimmedName) patch.name = trimmedName;
  return patch;
}
